import Grid from '@precis-digital/kurama/Grid';
import { RechartsLegend as Legend, useRechartsChartHeight as useChartHeight, useRechartsChartWidth as useChartWidth, type RechartsLegendProps as LegendProps } from '@precis-digital/kurama/recharts';
import { styled, useTheme } from '@precis-digital/kurama/utils';
import Typography from '@precis-digital/kurama/Typography';
import { useCallback, useEffect, useMemo, useRef, type ReactElement, type ReactNode, type UIEventHandler } from 'react';
import type { ElementDescriptionMetric, ElementsDescriptionsMap, SecondAxisDetails } from '../../internalTypes';
import type { ChartCardChartDefinition, ChartCardTFunction, ChartDataRow, ViewType } from '../../types';
import { formatMetricValue } from '../../utils/generics';
import { fixLegendPayload, getLegendItemFormatter } from './utils';
import { useChartCardDefaults } from '../../hooks/useChartCardDefaults';
export const StatusLegendMinWidth = 290;
export const StatusLegendMaxWidth = 797;
interface ChartLegendProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> extends LegendProps {
  viewType: ViewType;
  chartWidth: number;
  chart: ChartCardChartDefinition<DataKey, DataRow>;
  firstAxisDataKey: DataKey;
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
  secondAxisDetails: Array<SecondAxisDetails<DataKey, DataRow>>;
  hoveredSlice?: string | null;
  t: ChartCardTFunction;
}
const StatusLegend = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  viewType,
  chartWidth: chartWidthProp,
  chart,
  firstAxisDataKey,
  elementsDescriptionsMap,
  secondAxisDetails,
  hoveredSlice,
  t,
  ...props
}: ChartLegendProps<DataKey, DataRow>): ReactNode => {
  const defaults = useChartCardDefaults();
  const chartHeight = useChartHeight();
  const chartWidth = useChartWidth();
  const theme = useTheme();
  const legendContentScrollableRef = useRef<HTMLDivElement | null>(null);
  const legendScrollTopRef = useRef(0);
  const rowsRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
  const setRowRef = useCallback((id: string) => {
    return (el: HTMLTableRowElement | null | undefined) => {
      const map = rowsRefs.current;
      if (el == null) {
        map.delete(id);
      } else {
        map.set(id, el);
      }
    };
  }, []);
  const handleLegendScroll = useCallback<UIEventHandler<HTMLDivElement>>(event => {
    legendScrollTopRef.current = event.currentTarget.scrollTop;
  }, []);
  const hoveredSliceRE = useMemo(() => hoveredSlice == null ? null : new RegExp(String.raw`\b${hoveredSlice}$`), [hoveredSlice]);
  useEffect(() => {
    if (legendContentScrollableRef.current != null) {
      legendContentScrollableRef.current.scrollTop = legendScrollTopRef.current;
    }
    if (hoveredSliceRE == null) {
      return;
    }
    for (const [id_0, row] of rowsRefs.current.entries()) {
      if (hoveredSliceRE.test(id_0)) {
        row.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
        break;
      }
    }
  }, [hoveredSliceRE]);
  if (viewType !== 'polar') {
    return null;
  }
  const legendSpace = chartWidth == null ? StatusLegendMinWidth : Math.max(chartWidth - chartWidthProp, StatusLegendMinWidth);
  const legendWidth = Math.min(legendSpace, StatusLegendMaxWidth);
  const legendMarginRight = Math.max(0, legendSpace - legendWidth);
  const legendPaddingRight = Math.max(40 - legendMarginRight, 0);
  const formatter = getLegendItemFormatter({
    defaults,
    dimensionKey: chart.firstAxis,
    elementsDescriptionsMap,
    hoveredSlice,
    setRowRef,
    t
  });
  const content: LegendProps['content'] = contentProps => {
    const {
      payload,
      dataKeysTotals,
      dataKeys
    } = fixLegendPayload({
      secondAxisDetails,
      legendPayload: contentProps.payload
    });
    const multiple = dataKeys.length > 1;
    const elements = dataKeys.map<ReactElement>((dataKey, index) => {
      const metricDescription = elementsDescriptionsMap[dataKey] as ElementDescriptionMetric;
      const totalFormatted = dataKeysTotals == null ? '--' : formatMetricValue(dataKey, metricDescription, dataKeysTotals[index], defaults.axis.format);
      if (multiple) {
        return <Grid key={dataKey} item container alignItems="center" gap={theme.spacing(1)} direction="row">
            <Typography key="label" variant="h2" color="primary">
              {dataKey}
            </Typography>
            <Typography key="value" variant="h2" color="primary">
              {totalFormatted}
            </Typography>
          </Grid>;
      }
      return <LegendContentTotalRow key={`${dataKey}-value`}>
          <Typography key={`${dataKey}-value`} variant="h1" color="primary">
            {totalFormatted}
          </Typography>
        </LegendContentTotalRow>;
    });
    return <Grid container direction="column" gap={theme.spacing(5)} height={`${chartHeight}px`} paddingRight={`${legendPaddingRight}px`} width={`${legendWidth}px`} marginRight={`${legendMarginRight}px`} wrap="nowrap">
        <Grid item height="content">
          {multiple ? <LegendContentTotals>{elements}</LegendContentTotals> : elements}
        </Grid>
        <LegendContentScrollable ref={legendContentScrollableRef} item direction="column" alignItems="center" onScroll={handleLegendScroll}>
          <LegendContentPayload>
            {payload?.flatMap((entry, index_0) => formatter(entry, index_0 === payload.length - 1))}
          </LegendContentPayload>
        </LegendContentScrollable>
      </Grid>;
  };
  return <Legend layout="vertical" align="left" verticalAlign="middle" content={content} itemSorter={() => 0} {...props} />;
};
const LegendContentScrollable = styled(Grid)(() => ({
  flexGrow: 1,
  minHeight: 0,
  overflowY: 'auto'
}));
const LegendContentTotals = styled('table')(() => ({
  borderCollapse: 'collapse'
}));
const LegendContentPayload = styled('table')(() => ({
  borderCollapse: 'collapse',
  width: '100%'
}));
const LegendContentTotalRow = styled('tr')(({
  theme
}) => ({
  width: '100%',
  columnSpan: 'all',
  height: theme.spacing(8)
}));
export default StatusLegend;